import { createPortal } from 'react-dom';

import { JSX, RefObject, useLayoutEffect, useMemo, useReducer, useRef } from 'react';

import { OrgChart as D3OrgChart } from 'd3-org-chart';
import { withStyles } from 'vopay';

import { buildChartRows, ChartRowsOptions } from './orgChartTree';
import { OrgChartTreeNode } from './types';
import styles from './OrgChart.module.scss';

const cn = withStyles(styles);

export type OrgChartProps<T extends OrgChartTreeNode<T>> = ChartRowsOptions<T> & {
  // The host's whole tree; undefined until it's built.
  tree: T | undefined;
  // Rendered into the node's card through a portal, so it sits in the host's React tree and its providers.
  renderComponent: (node: T) => JSX.Element;
  // A click anywhere in the node's card that no card control stopped (React propagation).
  onNodeClick: (node: T) => void;
  nodeWidth?: (node: T) => number;
  nodeHeight?: (node: T) => number;
  childrenMargin?: number;
  siblingsMargin?: number;
  neighborMargin?: number;
  // Off, the wheel over the chart does nothing at all: it neither zooms the chart nor scrolls the page.
  isWheelZoomEnabled?: boolean;
  // The host keeps the d3 chart instance to call it directly (e.g. setCentered).
  chartRef: RefObject<D3OrgChart<T> | null>;
};

const TRANSITION_DURATION = 400;

// d3-zoom scales by 2^delta per wheel event: its default delta is about 15% a notch (ten times that on a pinch or
// Ctrl+wheel), which overshoots a chart of fixed-size cards. This takes a fraction of it.
const WHEEL_ZOOM_RATE = 0.4;

function wheelDelta(event: WheelEvent) {
  const lineOrPage = event.deltaMode === 1 ? 0.05 : 1;
  const unit = event.deltaMode === 0 ? 0.002 : lineOrPage;

  return -event.deltaY * unit * (event.ctrlKey ? 10 : 1) * WHEEL_ZOOM_RATE;
}

// d3-org-chart removes a card that leaves the chart in its exit transition's "end" handler. If a re-render brings
// that card back during the transition's last frame (e.g. paging back to cards that just paged out), the new
// render's transition hasn't started yet to interrupt it, so the stale handler still fires and removes a card the
// new render kept. Interrupting the cards' in-flight transitions first cancels those handlers; the new render then
// exits whatever it no longer shows.
function renderChart<T>(chart: D3OrgChart<T>) {
  chart.getChartState().nodesWrapper?.selectAll('g.node').interrupt();
  chart.render();
}

/**
 * Where a node's card lives. d3-org-chart rewrites every node's foreignObject HTML on each render(), so a card
 * rendered straight into it would be torn down and remounted a frame later every time the chart moves (a blank
 * frame, and every card's React state lost). Instead each node owns one host element for its lifetime; the chart
 * callbacks re-append it into the fresh wrapper, and the card inside it — a portal from this component — carries
 * on untouched.
 */
function createHost() {
  const host = document.createElement('div');

  host.className = cn('host');

  return host;
}

export default function OrgChart<T extends OrgChartTreeNode<T>>({
  tree,
  pageSize,
  displayLevel,
  keepChildrenWhole,
  getExtraChildren,
  nodeWidth,
  nodeHeight,
  childrenMargin = 40,
  siblingsMargin = 30,
  neighborMargin = 20,
  isWheelZoomEnabled = false,
  renderComponent,
  onNodeClick,
  chartRef,
}: OrgChartProps<T>): JSX.Element {
  const d3Container = useRef<HTMLDivElement>(null);

  const data = useMemo(
    () => (tree ? buildChartRows(tree, { pageSize, displayLevel, keepChildrenWhole, getExtraChildren }) : []),
    [tree, pageSize, displayLevel, keepChildrenWhole, getExtraChildren],
  );

  const hosts = useRef(new Map<string, HTMLDivElement>());
  // Nodes fading out of the chart keep their card until d3 removes them, as the last data they were drawn with.
  const exiting = useRef(new Map<string, T>());
  // The hosts exist only after the chart has drawn; this re-renders the portals once they do (and once they go).
  const [, hostsChanged] = useReducer((version: number) => version + 1, 0);

  const lastSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!data.length || !d3Container.current) return;

    if (!chartRef.current) {
      chartRef.current = new D3OrgChart();
      chartRef.current.container(d3Container.current as unknown as string);

      // The zoom behaviour is built on the first draw, so its wheel step is set before it.
      if (isWheelZoomEnabled) {
        const createDefaultZoom = chartRef.current.createZoom();

        chartRef.current.createZoom((node) => createDefaultZoom(node).wheelDelta(wheelDelta));
      }
    }

    const chart = chartRef.current;

    // `this` is the node's <g>, as d3's selection.each binds it; d3-org-chart's types leave that out.
    function attachHost(this: SVGGElement, node: T) {
      const wrapper = this.querySelector('.node-foreign-object-div');

      if (!wrapper) return;

      let host = hosts.current.get(node.id);

      if (!host) {
        host = createHost();
        hosts.current.set(node.id, host);
      }

      if (host.parentElement !== wrapper) wrapper.replaceChildren(host);
    }

    chart
      .container(d3Container.current as unknown as string) // d3-org-chart types are wrong here does accept element ref and string
      .layout('top')
      .compact(false)
      .nodeWidth((d) => (nodeWidth ? nodeWidth(d.data) : 90)) // Use the custom nodeWidth
      .nodeHeight((d) => (nodeHeight ? nodeHeight(d.data) : 120)) // Use the custom nodeHeight
      .childrenMargin(() => childrenMargin)
      .siblingsMargin(() => siblingsMargin)
      .neighbourMargin(() => neighborMargin)
      // The card wrapper's own React onClick reports the click (see the portal below): d3's handler sits on the
      // <g>, below the React root, so a card control's stopPropagation could never reach it.
      .onNodeClick(() => {})
      .nodeButtonHeight(() => 0)
      .nodeButtonWidth(() => 0)
      .buttonContent(() => {
        return ``;
      })
      .linkUpdate((node, index, nodes) => {
        const currentNode = nodes[index] as unknown as SVGPathElement; // don't know why types are wrong here

        if (node.data.linked) {
          currentNode.setAttribute('fill', 'none');
          currentNode.setAttribute('stroke', '#EFEFEF');
          currentNode.setAttribute('stroke-width', '2');
        }
      })
      .linkYOffset(0)
      .duration(TRANSITION_DURATION)
      // Empty on purpose: nodeUpdate / nodeExit fill the wrapper with the node's host.
      .nodeContent(() => '')
      .nodeUpdate(function (this: SVGGElement, d) {
        exiting.current.delete(d.data.id);
        attachHost.call(this, d.data);
      })
      .nodeExit(function (this: SVGGElement, d) {
        exiting.current.set(d.data.id, d.data);
        attachHost.call(this, d.data);

        // d3 removes the <g> when the exit transition ends. A node that came back meanwhile (nodeUpdate ran again)
        // is still connected and keeps its host.
        window.setTimeout(() => {
          const host = hosts.current.get(d.data.id);

          if (!host || host.isConnected) return;

          hosts.current.delete(d.data.id);
          exiting.current.delete(d.data.id);
          hostsChanged();
        }, TRANSITION_DURATION + 50);
      })
      // Every row arrives expanded (d3-org-chart's own flag) so one render draws the whole tree.
      .data(data.map((row) => ({ ...row, _expanded: true })));

    renderChart(chart);
    hostsChanged();
  }, [data]);

  useLayoutEffect(() => {
    if (!d3Container.current) return;

    // Threshold to ignore small changes (like scrollbar appearing/disappearing)
    const RESIZE_THRESHOLD = 20; // pixels

    const resizeObserver = new ResizeObserver((entries) => {
      const chart = chartRef.current;
      if (!chart) return;

      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        const lastSize = lastSizeRef.current;

        // Calculate the difference from last known size
        const widthDiff = Math.abs(width - lastSize.width);
        const heightDiff = Math.abs(height - lastSize.height);

        // Only re-render if the change is significant (larger than scrollbar width)
        if (widthDiff > RESIZE_THRESHOLD || heightDiff > RESIZE_THRESHOLD) {
          lastSizeRef.current = { width, height };
          renderChart(chart.container(d3Container.current as unknown as string));
        }
      });
    });

    resizeObserver.observe(d3Container.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    // d3-zoom handles the wheel itself (zooming, and keeping the page from scrolling) unless this stops it first.
    if (isWheelZoomEnabled) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const container = d3Container.current;

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel, { capture: true });
      }
    };
  }, [isWheelZoomEnabled]);

  // The drawn rows first, then the ones on their way out (a row that came back is drawn, not exiting — the chart
  // has not run yet on the render where it returns); a node without a host has not been drawn yet.
  const drawnIDs = new Set(data.map((node) => node.id));
  const exitingNodes = [...exiting.current.values()].filter((node) => !drawnIDs.has(node.id));

  const cards = [...data, ...exitingNodes].flatMap((node) => {
    const host = hosts.current.get(node.id);

    return host ? [{ node, host }] : [];
  });

  return (
    <>
      <div ref={d3Container} />

      {cards.map(({ node, host }) =>
        createPortal(
          <div className={cn('card')} onClick={() => onNodeClick(node)}>
            {renderComponent(node)}
          </div>,
          host,
          node.id,
        ),
      )}
    </>
  );
}
